import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, HeartPulse, Brain, Apple, Pill as PillBottle, Stethoscope, BookHeart, Cross, Ambulance } from '../lib/icons';
import { useSearch } from '../hooks/useSearch';
import { supabase } from '../lib/supabase';

function roundDown(n: number, step: number): number {
  if (!n || n <= 0) return 0;
  return Math.floor(n / step) * step;
}

const Hero: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { performSearch } = useSearch();
  const navigate = useNavigate();

  const [providerCount, setProviderCount] = useState<number | null>(null);
  const [topicCount, setTopicCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [doctorsRes, topicsRes, blogRes] = await Promise.all([
          supabase.from('doctors').select('*', { count: 'exact', head: true }).eq('status', 'active'),
          supabase.from('topics').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('blog_posts').select('*', { count: 'exact', head: true }).eq('published', true),
        ]);
        if (cancelled) return;
        setProviderCount(doctorsRes.count ?? 0);
        setTopicCount((topicsRes.count ?? 0) + (blogRes.count ?? 0));
      } catch (err) {
        console.warn('Hero counters: stats query failed, falling back to placeholders', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const providerLabel = providerCount === null
    ? '...'
    : providerCount >= 100
      ? `${roundDown(providerCount, 100).toLocaleString('de-DE')}+`
      : `${providerCount}`;
  const topicLabel = topicCount === null
    ? '...'
    : topicCount >= 100
      ? `${roundDown(topicCount, 100).toLocaleString('de-DE')}+`
      : `${topicCount}`;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      performSearch(searchTerm.trim());
      navigate('/search');
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'diseases':
        navigate('/krankheiten');
        break;
      case 'doctors':
        navigate('/anbieter');
        break;
      default:
        break;
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white bg-opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white bg-opacity-10 rounded-full blur-3xl"></div>
      </div>

      {/* Removed old global floating icons; icons are now bound to teaser images below */}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 items-center">

          {/* Right Column - Visual Elements - Now First on Mobile */}
          <div className="relative order-1 lg:order-2">
            {/* Treatment Teaser Images */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
              {/* Top Left - Behandlung Teaser 1 */}
              <div className="relative">
                <img
                  src="/teaser_01_neu.webp"
                  alt="Medizinische Behandlung und Gesundheitsversorgung"
                  className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover rounded-2xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-300"
                  loading="eager"
                  fetchpriority="high"
                />
                {/* Icons for Teaser 1 */}
                <div className="pointer-events-none select-none absolute -top-3 -left-3 sm:-top-4 sm:-left-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-red-400 to-pink-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0s' }} aria-hidden>
                    <HeartPulse className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
                <div className="pointer-events-none select-none absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-violet-400 to-indigo-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0.6s' }} aria-hidden>
                    <Brain className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Top Right - Behandlung Teaser 2 */}
              <div className="relative mt-4 sm:mt-6 lg:mt-8">
                <img
                  src="/teaser_02_neu.webp"
                  alt="Gesunde Ernährung und Prävention"
                  className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover rounded-2xl shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-300"
                  loading="eager"
                  fetchpriority="high"
                />
                {/* Icons for Teaser 2 */}
                <div className="pointer-events-none select-none absolute -top-3 -right-3 sm:-top-4 sm:-right-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0.2s' }} aria-hidden>
                    <Apple className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
                <div className="pointer-events-none select-none absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0.8s' }} aria-hidden>
                    <PillBottle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Bottom Left - Behandlung Teaser 3 */}
              <div className="relative -mt-2 sm:-mt-3 lg:-mt-4">
                <img
                  src="/teaser_03_neu.webp"
                  alt="Ärztliche Untersuchung und Diagnostik"
                  className="w-full h-32 sm:h-40 md:h-48 lg:h-56 object-cover rounded-2xl shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-300"
                  loading="lazy"
                />
                {/* Icons for Teaser 3 */}
                <div className="pointer-events-none select-none absolute -top-3 -left-3 sm:-top-4 sm:-left-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-sky-400 to-cyan-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0.4s' }} aria-hidden>
                    <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
                <div className="pointer-events-none select-none absolute bottom-2 right-2 sm:bottom-3 sm:right-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-400 to-cyan-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '1s' }} aria-hidden>
                    <BookHeart className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Bottom Right - Behandlung Teaser 4 */}
              <div className="relative">
                <img
                  src="/teaser_04_neu.webp"
                  alt="Medizinische Versorgung und Pflege"
                  className="w-full h-32 sm:h-40 md:h-48 lg:h-56 object-cover rounded-2xl shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-300"
                  loading="lazy"
                />
                {/* Icons for Teaser 4 */}
                <div className="pointer-events-none select-none absolute -top-3 -right-3 sm:-top-4 sm:-right-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-rose-400 to-red-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '0.5s' }} aria-hidden>
                    <Cross className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
                <div className="pointer-events-none select-none absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-fuchsia-400 to-purple-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/50 animate-float" style={{ animationDelay: '1.2s' }} aria-hidden>
                    <Ambulance className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Left Column - Content - Now Second on Mobile */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-5 lg:mb-6">
              Ihr Wegweiser zu{' '}
              <span className="text-blue-200">Gesundheit</span> und{' '}
              <span className="text-blue-200">medizinischer Hilfe.</span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 mb-6 sm:mb-7 lg:mb-8 leading-relaxed">
              Fundierte Informationen zu Krankheiten und qualifizierte Ärzte in Ihrer Nähe – alles auf einer Plattform.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6 sm:mb-7 lg:mb-8">
              <div className="relative max-w-2xl mx-auto lg:mx-0">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ärzte, Fachrichtungen, Städte, Krankheiten oder Symptome suchen..."
                  className="w-full px-4 sm:px-5 lg:px-6 py-3 sm:py-3.5 lg:py-4 pr-14 sm:pr-16 text-gray-900 bg-white rounded-2xl shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300 text-base sm:text-lg"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-2.5 sm:p-3 rounded-xl hover:bg-blue-700 transition-colors"
                  aria-label="Suche starten"
                >
                  <Search className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </form>

            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <button
                onClick={() => handleQuickAction('diseases')}
                className="flex items-center justify-center px-6 sm:px-7 lg:px-8 py-3 sm:py-3.5 lg:py-4 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-xl hover:bg-opacity-30 transition-all duration-200 font-semibold text-base sm:text-lg"
                aria-label="Zu Krankheiten navigieren"
              >
                Krankheiten
              </button>
              <button
                onClick={() => handleQuickAction('doctors')}
                className="flex items-center justify-center px-6 sm:px-7 lg:px-8 py-3 sm:py-3.5 lg:py-4 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-xl hover:bg-opacity-30 transition-all duration-200 font-semibold text-base sm:text-lg"
                aria-label="Anbieter in Ihrer Nähe finden"
              >
                Anbieter finden
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-10 sm:mt-12 lg:mt-16 xl:mt-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <div className="text-3xl sm:text-4xl font-bold mb-2">{providerLabel}</div>
              <div className="text-blue-100">Gesundheitsanbieter</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <div className="text-3xl sm:text-4xl font-bold mb-2">{topicLabel}</div>
              <div className="text-blue-100">Gesundheitsthemen</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <div className="text-3xl sm:text-4xl font-bold mb-2">24/7</div>
              <div className="text-blue-100">Erreichbarkeit</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;